// ConversationsList.jsx — proc-v3 (F2.3e): QUARTA VISTA DEL MONOLITE in React,
// lista "Conversazioni", flag-gated (shell_proc_v3_conversations, default OFF).
//
// Replica 1:1 renderConversations di proc-v0.js:7024-7095: stesso titolo/
// sottotitolo, stesso bottone "+ Nuova conversazione", stesso select di stato
// (6 option, 'Archiviate' hardcoded come nel v1), stesso conteggio a destra,
// stesse colonne (Contatto+lingua, Fornitore, Oggetto a due righe, Collegato a
// con badge campagne, Ultima attività con pallino "ricevuto", Stato badge,
// Tag con azioni inline), stessa evidenziazione ambra delle conversazioni in
// entrata non chiuse (prop rowClass del DataTable, estensione F2.3e), stesso
// ordinamento di default (Ultima attività desc — parità byte-per-byte via
// rank v1), stesso click riga → MODALE del monolite (openConversationModal).
//
// ACCOPPIAMENTI (studio in api.js, sezione F2.3e): badge nav e poller globale
// restano SEMPRE v1 (vivono fuori dalla vista). Il redraw del poller arriva
// qui attraverso il wrap (tick della factory). MAI 'nav:badge' da questo modulo.
//
// AZIONI (payload identici): tag add/del = confirmWrite.conversation del
// monolite (v1:7087-7088), redraw solo su r.ok. Modali nuova conversazione e
// dettaglio = V1 (deleghe in api.js).
//
// STATO (parità di persistenza col v1): il filtro di stato vive nella FACTORY
// (pagina), come il binding lessicale convFilters del v1 (proc-v0.js:7023):
// sopravvive a nav via/ritorno e ai refresh dati. NB: binding LESSICALE nel
// v1 ⇒ v1 e v3 non condividono il valore corrente, stesso default 'all'
// (stessa deviazione dichiarata 5 di f23d per le campagne).
//
// Il chrome usa le stesse classi Tailwind del v1 (il componente vive nel
// documento del monolite, #conversations-content) — parità visiva senza fork.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tConv, CONV_TEXTS, defaultConvFilters, readConversations, convRowClass,
  convSubjectParts, convContactFilterText, convSubjectFilterText,
  convCampaignsFilterText, convLastActivityFilterText, convStateFilterText,
  stateBadgeClass, stateLabel,
  openConversation, openNewConversation, addConvTag, removeConvTag,
} from './api.js';

export function ConversationsList({ win, tick, filters }) {
  // filtro di vista: stato React specchiato sull'oggetto della factory (write-through)
  const [f, setFState] = React.useState(() => ({ ...filters }));
  const setF = (patch) => {
    Object.assign(filters, patch);
    setFState({ ...filters });
  };

  // dati: letti dallo store del monolite a ogni refresh (tick dal wrapper —
  // compresi i giri del poller globale v1) o cambio filtro
  const data = React.useMemo(
    () => readConversations(win, f),
    [win, tick, f.state],
  );

  const t = (k) => tConv(win, k);

  const columns = [
    {
      key: '_contactName',
      label: t('col.contact'),
      sortable: true,
      sortValue: (r) => r._v1Rank._contactName, // rank v1 ⇒ ordine byte-per-byte anche al click
      filterText: convContactFilterText,
      render: (r, ctx) => (
        <>
          <div className="font-medium"><ctx.Mark text={r._contactName} tokens={ctx.tokens} /></div>
          {r._contactLang ? (
            <div className="text-xs text-slate-500"><ctx.Mark text={r._contactLang} tokens={ctx.tokens} /></div>
          ) : null}
        </>
      ),
    },
    {
      key: '_supplierName',
      label: t('col.supplier'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supplierName, // rank v1
      render: (r, ctx) => <span className="text-xs"><ctx.Mark text={r._supplierName} tokens={ctx.tokens} /></span>,
    },
    {
      key: 'subject',
      label: t('col.subject'),
      sortable: true,
      sortValue: (r) => r._v1Rank.subject, // rank v1 (sul subject RAW, come il v1)
      filterText: convSubjectFilterText,
      render: (r, ctx) => {
        const p = convSubjectParts(r);
        return (
          <>
            <div className="font-medium"><ctx.Mark text={p.primary} tokens={ctx.tokens} /></div>
            <div className="text-xs text-slate-500"><ctx.Mark text={p.secondary} tokens={ctx.tokens} /></div>
          </>
        );
      },
    },
    {
      key: '_campaignsText',
      label: t('col.linked_to'),
      sortable: true,
      sortValue: (r) => r._v1Rank._campaignsText, // rank v1 (join ', ' del campo riga)
      filterText: convCampaignsFilterText,
      render: (r, ctx) => (r._campaigns.length ? (
        <>
          {r._campaigns.map((c, i) => (
            <span key={i} className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded mr-1">
              <ctx.Mark text={c.name} tokens={ctx.tokens} />
            </span>
          ))}
        </>
      ) : (
        <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded">
          <ctx.Mark text={CONV_TEXTS.noCampaign} tokens={ctx.tokens} />
        </span>
      )),
    },
    {
      key: '_lastActivityRel',
      label: t('col.last_activity'),
      sortable: true,
      sortValue: (r) => r._v1Rank._lastActivityRel, // rank v1 (type date su lastActivityAt)
      filterText: convLastActivityFilterText,
      render: (r, ctx) => (
        <span className="text-xs">
          {r.lastDirection === 'in' ? (
            <span className="inline-flex items-center justify-center bg-rose-500 text-white rounded-full w-4 h-4 text-[9px] align-middle">●</span>
          ) : null}
          <ctx.Mark
            text={(r.lastDirection === 'in' ? ' ' + CONV_TEXTS.receivedWord : '→') + ' ' + r._lastActivityRel}
            tokens={ctx.tokens}
          />
        </span>
      ),
    },
    {
      key: '_stateText',
      label: t('col.state'),
      sortable: true,
      sortValue: (r) => r._v1Rank._stateText, // rank v1 (stato RAW, non la label)
      filterText: (r) => convStateFilterText(win, r),
      render: (r, ctx) => (
        <span className={stateBadgeClass(r.state) + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={stateLabel(win, r.state)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: 'tags',
      label: CONV_TEXTS.tagsCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.tags, // rank v1 (String(array), come il v1)
      filter: false, // parità v1:7072 — la colonna Tag non ha filtro
      render: (r) => {
        const tags = Array.isArray(r.tags) ? r.tags : [];
        return (
          <div className="flex flex-wrap gap-1 items-center">
            {tags.map((tg, i) => (
              <span key={i} className="bg-indigo-100 text-indigo-800 text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                {tg}
                <button
                  type="button"
                  className="cv-tag-del text-indigo-400 hover:text-rose-600 leading-none"
                  title={CONV_TEXTS.removeTagTitle}
                  onClick={(e) => { e.stopPropagation(); removeConvTag(win, r.id, tg); }}
                >×</button>
              </span>
            ))}
            <button
              type="button"
              className="cv-tag-add text-[10px] text-cyan-700 hover:underline"
              onClick={(e) => { e.stopPropagation(); addConvTag(win, r.id); }}
            >{CONV_TEXTS.addTagBtn}</button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-bold">{t('conv.title')}</h1>
        <button
          id="conv-new-btn"
          type="button"
          className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded text-sm font-medium"
          onClick={() => openNewConversation(win)}
        >{CONV_TEXTS.newBtn}</button>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t('conv.subtitle')}</p>
      <div className="bg-white rounded-lg shadow border border-slate-200 p-3 mb-3 flex flex-wrap gap-2 items-center text-sm filter-bar">
        <select
          id="conv-filter-state"
          className="border rounded px-2 py-1.5"
          value={f.state}
          onChange={(e) => setF({ state: e.target.value })}
        >
          <option value="all">{t('conv.all_states')}</option>
          <option value="awaiting-decision">{t('conv.awaiting_decision')}</option>
          <option value="active">{t('conv.active')}</option>
          <option value="waiting">{t('conv.waiting_reply')}</option>
          <option value="closed">{t('conv.closed')}</option>
          <option value="archived">{CONV_TEXTS.archivedOption}</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{data.rows.length + ' ' + t('conv.title').toLowerCase()}</span>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3" id="conv-table-mount">
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey="id"
          sort={{ key: '_lastActivityRel', dir: 'desc' }}
          filterable
          rowClass={convRowClass}
          onRowClick={(row) => openConversation(win, row.id)}
          emptyMessage={CONV_TEXTS.emptyMessage}
        />
      </div>
    </div>
  );
}

// Factory (pattern createProcV3Campaigns): il register/install la aggancia al
// WRAP di window.renderConversations. render() idempotente sul container;
// unmount() libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Conversations(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  // filtro di vista a livello di PAGINA, come convFilters v1 (proc-v0.js:7023)
  const filters = defaultConvFilters();

  function render() {
    const container = win.document.getElementById('conversations-content');
    if (!container) throw new Error('#conversations-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (poller, modali v1, rientro)
    root.render(<ConversationsList win={win} tick={tick} filters={filters} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
    // filters NON si azzerano: parità col v1 (binding di pagina)
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
